import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";

function Part({ t, dx = 0, dy = 0, rot = 0, children }) {
  const x = useTransform(t, [0, 1], [0, dx]);
  const y = useTransform(t, [0, 1], [0, dy]);
  const rotate = useTransform(t, [0, 1], [0, rot]);
  return <motion.g style={{ x, y, rotate, transformBox: "fill-box", transformOrigin: "center" }}>{children}</motion.g>;
}

const GOLD = "#C9A46A";
const SHELL = "#FFFDF7";
const INK = "#3A2E25";

function MacBookExploded({ t }) {
  const labelOp = useTransform(t, [0.55, 1], [0, 1]);
  return (
    <svg viewBox="0 0 1000 640" className="w-[min(94vw,640px)] overflow-visible" fill="none" aria-hidden="true" style={{ overflow: "visible" }}>
      {/* bottom case */}
      <Part t={t} dy={330} rot={-2}>
        <path d="M112 486 H888 L946 536 Q948 548 934 548 H66 Q52 548 54 536 Z" fill="#F3EAE0" stroke={GOLD} strokeWidth="1.5" />
        {[[140, 540], [860, 540], [300, 544], [700, 544]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="3" fill={GOLD} />)}
        <text x="500" y="530" textAnchor="middle" fill="#B9A896" fontSize="9" fontFamily="Jost, sans-serif" letterSpacing="3">BOTTOM CASE</text>
      </Part>
      {/* battery cells */}
      <Part t={t} dx={150} dy={230} rot={4}>
        {[0, 1, 2].map((i) => <rect key={i} x={520 + i * 92} y="486" width="84" height="44" rx="8" fill={INK} stroke={GOLD} strokeWidth="1.2" />)}
        {[0, 1, 2].map((i) => <rect key={i} x={532 + i * 92} y="496" width="60" height="6" rx="3" fill="#57473B" />)}
      </Part>
      {/* logic board */}
      <Part t={t} dx={-170} dy={220} rot={-5}>
        <rect x="160" y="482" width="320" height="52" rx="6" fill="#2E3A2A" stroke={GOLD} strokeWidth="1.2" />
        {[0, 1, 2, 3, 4, 5].map((i) => <rect key={i} x={176 + i * 50} y="494" width={i % 2 ? 22 : 30} height="28" rx="3" fill={i === 2 ? INK : "#4A5A44"} stroke={GOLD} strokeWidth="0.8" />)}
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <line key={i} x1={170 + i * 40} y1="486" x2={190 + i * 40} y2="530" stroke={GOLD} strokeWidth="0.5" opacity="0.6" />)}
      </Part>
      {/* speakers */}
      <Part t={t} dx={-110} dy={110}>
        <rect x="128" y="490" width="70" height="40" rx="6" fill={SHELL} stroke={GOLD} />
        {[0, 1, 2, 3].map((r) => [0, 1, 2, 3, 4].map((c) => <circle key={`${r}${c}`} cx={140 + c * 11} cy={498 + r * 8} r="1.6" fill={GOLD} />))}
      </Part>
      <Part t={t} dx={110} dy={110}>
        <rect x="802" y="490" width="70" height="40" rx="6" fill={SHELL} stroke={GOLD} />
        {[0, 1, 2, 3].map((r) => [0, 1, 2, 3, 4].map((c) => <circle key={`${r}${c}`} cx={814 + c * 11} cy={498 + r * 8} r="1.6" fill={GOLD} />))}
      </Part>
      {/* trackpad */}
      <Part t={t} dy={150} dx={40} rot={3}>
        <path d="M430 516 H570 L574 534 H426 Z" fill={SHELL} stroke={GOLD} strokeWidth="1.2" />
      </Part>
      {/* top case + keyboard */}
      <Part t={t} dy={70} rot={1.5}>
        <path d="M110 476 H890 L950 528 Q952 540 938 540 H62 Q48 540 50 528 Z" fill="#E8DCCB" stroke={GOLD} strokeWidth="1.2" />
        <path d="M232 482 H768 L784 512 H216 Z" fill="#DCCDBB" />
        {Array.from({ length: 4 }).map((_, r) =>
          Array.from({ length: 20 }).map((_, c) => (
            <rect key={`${r}-${c}`} x={240 + c * 26 + r * 2.2} y={485 + r * 7} width="20" height="5" rx="1.2" fill={SHELL} />
          ))
        )}
      </Part>
      {/* hinge */}
      <Part t={t} dy={18}>
        <rect x="170" y="466" width="660" height="10" rx="3" fill="#2A211A" stroke={GOLD} strokeWidth="0.8" />
      </Part>
      {/* lid shell (back) */}
      <Part t={t} dy={-210} rot={-1}>
        <rect x="170" y="52" width="660" height="418" rx="26" fill={INK} stroke={GOLD} strokeWidth="1.5" />
        <rect x="190" y="72" width="620" height="378" rx="14" fill="#2A211A" />
        <text x="500" y="270" textAnchor="middle" fill="#8C7562" fontSize="10" fontFamily="Jost, sans-serif" letterSpacing="4">LID · ALUMINUM</text>
      </Part>
      {/* display panel with UI */}
      <Part t={t} dy={-70}>
        <rect x="186" y="68" width="628" height="386" rx="10" fill={SHELL} stroke={GOLD} strokeWidth="1" />
        <rect x="186" y="68" width="628" height="26" rx="10" fill="#F3EAE0" />
        <rect x="186" y="84" width="628" height="10" fill="#F3EAE0" />
        <circle cx="206" cy="81" r="3.5" fill="#D4A0B4" />
        <circle cx="218" cy="81" r="3.5" fill="#C39B7B" />
        <circle cx="230" cy="81" r="3.5" fill="#A8785A" />
        <rect x="198" y="106" width="70" height="336" rx="8" fill="#FFF3F8" />
        {[0, 1, 2, 3, 4].map((i) => (
          <rect key={i} x="210" y={122 + i * 26} width={i === 0 ? 46 : 34} height="8" rx="4" fill={i === 0 ? "#A8785A" : "#E8DCCB"} />
        ))}
        <rect x="284" y="106" width="518" height="336" rx="8" fill="#FAF6EF" />
        {Array.from({ length: 13 }).map((_, i) => (
          <line key={i} x1={284 + i * 43} y1="106" x2={284 + i * 43} y2="442" stroke="#E8DCCB" strokeWidth="0.6" opacity="0.6" />
        ))}
        {Array.from({ length: 8 }).map((_, i) => (
          <line key={i} x1="284" y1={106 + i * 48} x2="802" y2={106 + i * 48} stroke="#E8DCCB" strokeWidth="0.6" opacity="0.6" />
        ))}
        <path className="draw-path" d="M316 380 C 400 200, 500 410, 596 220 S 720 330, 770 170" stroke="#A8785A" strokeWidth="3.5" strokeLinecap="round" />
        {[[316, 380], [500, 410], [596, 220], [770, 170]].map(([x, y], i) => (
          <rect key={i} x={x - 6} y={y - 6} width="12" height="12" fill="#D4A0B4" stroke={INK} strokeWidth="1.5" />
        ))}
        <rect x="690" y="120" width="96" height="60" rx="8" fill={SHELL} stroke="#E8DCCB" />
        {["#FAF6EF", "#FFF3F8", "#C39B7B", "#A8785A"].map((c, i) => (
          <rect key={c} x={700 + i * 20} y="132" width="14" height="14" rx="3" fill={c} stroke="#E8DCCB" />
        ))}
      </Part>
      {/* front glass + notch */}
      <Part t={t} dy={-40} dx={-150} rot={-4}>
        <rect x="182" y="64" width="636" height="394" rx="12" fill="#FFFFFF" opacity="0.28" stroke={GOLD} strokeWidth="1.2" />
        <path d="M455 64 H545 V74 a8 8 0 0 1 -8 8 H463 a8 8 0 0 1 -8 -8 Z" fill={INK} />
        <circle cx="500" cy="73" r="2.5" fill="#57473B" />
        <line x1="200" y1="440" x2="800" y2="80" stroke="#FFFFFF" strokeWidth="18" opacity="0.18" />
      </Part>
      {/* screws & tiny parts */}
      {[[120, 300, -120, -40], [880, 300, 120, -40], [90, 420, -140, 80], [910, 420, 140, 80], [500, 600, 0, 120], [260, 610, -60, 100], [740, 610, 60, 100]].map(([x, y, dx, dy], i) => (
        <Part key={i} t={t} dx={dx} dy={dy} rot={90}>
          <circle cx={x} cy={y} r="5" fill={SHELL} stroke={GOLD} strokeWidth="1.2" opacity="0.001" />
          <motion.g style={{ opacity: t }}>
            <circle cx={x} cy={y} r="5" fill={SHELL} stroke={GOLD} strokeWidth="1.2" />
            <line x1={x - 3} y1={y} x2={x + 3} y2={y} stroke={GOLD} strokeWidth="1" />
          </motion.g>
        </Part>
      ))}
      <motion.g style={{ opacity: labelOp }}>
        <text x="40" y="30" fill="#B9A896" fontSize="10" fontFamily="Jost, sans-serif" letterSpacing="3">EXPLODED VIEW · MACBOOK PRO</text>
        <text x="960" y="30" textAnchor="end" fill="#B9A896" fontSize="10" fontFamily="Jost, sans-serif" letterSpacing="3">11 PARTS</text>
      </motion.g>
    </svg>
  );
}

function CircuitSVG() {
  const traces = [
    "M600 400 L600 220 L460 220 L460 90",
    "M600 400 L600 560 L740 560 L740 700",
    "M600 400 L380 400 L380 560 L200 560",
    "M600 400 L820 400 L820 240 L1000 240",
    "M600 400 L470 400 L470 620 L330 620",
    "M600 400 L730 400 L730 180 L880 180",
    "M600 400 L600 320 L520 320",
    "M600 400 L600 480 L690 480",
  ];
  const nodes = [[460, 90], [740, 700], [200, 560], [1000, 240], [330, 620], [880, 180], [520, 320], [690, 480]];
  return (
    <svg viewBox="0 0 1200 800" className="w-[150vmin]" fill="none" aria-hidden="true">
      {traces.map((d, i) => (
        <path key={i} d={d} stroke={i % 2 ? "#D4A0B4" : "#A8785A"} strokeWidth="2.5" opacity="0.9"
          style={{ filter: "drop-shadow(0 0 6px rgba(212,160,180,0.7))" }} />
      ))}
      {nodes.map(([x, y], i) => (
        <g key={i} className="pulse-node" style={{ animationDelay: `${i * 0.3}s` }}>
          <circle cx={x} cy={y} r="10" fill="#FFFDF7" stroke="#C48A9F" strokeWidth="3"
            style={{ filter: "drop-shadow(0 0 8px rgba(196,138,159,0.8))" }} />
          <circle cx={x} cy={y} r="3.5" fill="#A8785A" />
        </g>
      ))}
      <rect x="520" y="320" width="160" height="160" rx="12" fill="#3A2E25" stroke="#A8785A" strokeWidth="3"
        style={{ filter: "drop-shadow(0 0 18px rgba(168,120,90,0.45))" }} />
      <rect x="548" y="348" width="104" height="104" rx="6" fill="#2A211A" stroke="#57473B" strokeWidth="2" />
      <text x="600" y="394" textAnchor="middle" fill="#FFF3F8" fontSize="26" fontWeight="500"
        fontFamily="Olivera, 'Cormorant Garamond', Georgia, serif" letterSpacing="2">BA - 1</text>
      <text x="600" y="416" textAnchor="middle" fill="#C39B7B" fontSize="10"
        fontFamily="Jost, sans-serif" letterSpacing="2">ORGANIZER CORE</text>
      {Array.from({ length: 6 }).map((_, i) => (
        <g key={i}>
          <line x1={520} y1={340 + i * 24} x2={494} y2={340 + i * 24} stroke="#A8785A" strokeWidth="3" />
          <line x1={680} y1={340 + i * 24} x2={706} y2={340 + i * 24} stroke="#A8785A" strokeWidth="3" />
          <line x1={540 + i * 24} y1={320} x2={540 + i * 24} y2={294} stroke="#A8785A" strokeWidth="3" />
          <line x1={540 + i * 24} y1={480} x2={540 + i * 24} y2={506} stroke="#A8785A" strokeWidth="3" />
        </g>
      ))}
    </svg>
  );
}

export default function HeroTunnel() {
  const ref = useRef(null);
  const { pages } = useSite();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const p = useSpring(scrollYProgress, { stiffness: 80, damping: 22, mass: 0.4 });

  const explodeT = useTransform(p, [0.02, 0.2], [0, 1]);
  const compScale = useTransform(p, [0.2, 0.4], [1, 16]);
  const compOp = useTransform(p, [0.34, 0.41], [1, 0]);
  const introOp = useTransform(p, [0, 0.05], [1, 0]);
  const bezelScale = useTransform(p, [0.26, 0.5], [0.5, 24]);
  const bezelOp = useTransform(p, [0.26, 0.36, 0.46, 0.54], [0, 0.9, 0.9, 0]);
  const circScale = useTransform(p, [0.42, 0.78], [0.22, 4.6]);
  const circOp = useTransform(p, [0.4, 0.52, 0.72, 0.84], [0, 1, 1, 0]);
  const revOp = useTransform(p, [0.82, 0.93], [0, 1]);
  const revY = useTransform(p, [0.82, 1], [70, 0]);
  const revScale = useTransform(p, [0.82, 1], [0.94, 1]);
  const line1Y = useTransform(p, [0.84, 0.96], ["110%", "0%"]);
  const line2Y = useTransform(p, [0.87, 0.99], ["110%", "0%"]);
  const lineYs = [line1Y, line2Y];

  return (
    <section ref={ref} className="relative h-[500vh]" data-testid="hero-tunnel">
      <div className="sticky top-0 h-screen overflow-hidden bg-[#FAF6EF] blush-wash grain">
        <motion.div style={{ scale: compScale, opacity: compOp, transformOrigin: "50% 42%" }}
          className="absolute inset-0 flex items-center justify-center pt-[14vh]" data-testid="hero-stage-computer">
          <MacBookExploded t={explodeT} />
        </motion.div>

        <motion.div style={{ scale: bezelScale, opacity: bezelOp }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[46vmin] h-[30vmin] rounded-[3vmin] border-2 border-[#D4A0B4]"
            style={{ boxShadow: "0 0 60px rgba(212,160,180,0.5), inset 0 0 60px rgba(168,120,90,0.25)" }} />
        </motion.div>

        <motion.div style={{ scale: circScale, opacity: circOp }}
          className="absolute inset-0 flex items-center justify-center" data-testid="hero-stage-circuit">
          <CircuitSVG />
        </motion.div>

        <motion.div style={{ opacity: introOp }}
          className="absolute inset-x-0 bottom-10 flex flex-col items-center gap-3 pointer-events-none">
          <span className="eyebrow">Scroll to open the machine</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
            <ChevronDown size={20} className="text-[#A8785A]" />
          </motion.span>
        </motion.div>

        <motion.div style={{ opacity: introOp }} className="absolute top-24 inset-x-0 text-center pointer-events-none px-6">
          <p className="eyebrow mb-3">Business Aligned presents</p>
          <h1 className="f-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight">
            The <span className="f-serif text-[#A8785A]">Organizer</span>
          </h1>
        </motion.div>

        <motion.div style={{ opacity: revOp, y: revY, scale: revScale }}
          className="absolute inset-0 flex items-center" data-testid="hero-stage-reveal">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 w-full">
            <p className="eyebrow mb-5">Boot sequence complete — welcome inside</p>
            {["Your Vision. Your Business.", "One Private Workspace."].map((line, i) => (
              <div key={i} className="overflow-hidden">
                <motion.h2
                  style={{ y: lineYs[i] }}
                  className="f-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
                  {i === 1 ? <span className="f-serif text-[#A8785A]">{line}</span> : line}
                </motion.h2>
              </div>
            ))}
            <p className="text-[#8C7562] text-base max-w-2xl mt-6 leading-relaxed">
              A sophisticated command center for everything behind your business — from your brand and finances to your
              legal foundation, digital presence, and long-term vision. Your AI copilot keeps it all connected, organized,
              and moving forward.
            </p>
            <p className="f-serif text-[#A8785A] text-xl mt-4">Everything, thoughtfully in one place.</p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/organizer" className="btn-primary" data-testid="hero-cta-organizer">
                Open The Organizer <ArrowRight size={16} />
              </Link>
              {pages.websites && (
                <Link to="/websites" className="btn-ghost" data-testid="hero-cta-websites">
                  Our Work
                </Link>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
